import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import { colombianHolidayList } from "../../utils/businessDays";

// The institution's own working calendar, on top of the statutory holidays.
//
// Two lists, because both directions happen: the entity closes on days nobody
// legislated (a closure week, a local fair, a strike) and occasionally works a
// day the law calls a holiday. Every legal term in the app — the PQRSD SLA and
// the suspension of art. 17 today, anything else that counts business days
// later — is computed against this.

const YEAR_NOW = new Date().getFullYear();

const weekdayOf = (key) =>
  new Intl.DateTimeFormat("es-CO", { timeZone: "America/Bogota", weekday: "long" })
    .format(new Date(`${key}T17:00:00.000Z`));

const prettyDate = (key) =>
  new Intl.DateTimeFormat("es-CO", {
    timeZone: "America/Bogota", day: "numeric", month: "long", year: "numeric",
  }).format(new Date(`${key}T17:00:00.000Z`));

const isValidKey = (k) => /^\d{4}-\d{2}-\d{2}$/.test(k);

export default function InstitutionalCalendar({ calendar, onChange }) {
  const [year, setYear] = useState(YEAR_NOW);
  const [newDay, setNewDay] = useState("");
  const [kind, setKind] = useState("closedDays");
  const [error, setError] = useState(null);

  const closedDays = calendar?.closedDays || [];
  const openDays = calendar?.openDays || [];

  const holidays = useMemo(() => colombianHolidayList(year), [year]);
  const holidaySet = useMemo(() => new Set(holidays.map((h) => h.date)), [holidays]);

  const add = () => {
    const key = (newDay || "").slice(0, 10);
    if (!isValidKey(key)) {
      setError("Elige una fecha válida.");
      return;
    }
    if ((closedDays.includes(key) && kind === "closedDays")
      || (openDays.includes(key) && kind === "openDays")) {
      setError("Ese día ya está en la lista.");
      return;
    }
    setError(null);
    // A day can only be in one list; adding it to one removes it from the other.
    const other = kind === "closedDays" ? "openDays" : "closedDays";
    onChange({
      ...calendar,
      [kind]: [...(calendar?.[kind] || []), key].sort(),
      [other]: (calendar?.[other] || []).filter((d) => d !== key),
    });
    setNewDay("");
  };

  const remove = (list, key) =>
    onChange({ ...calendar, [list]: (calendar?.[list] || []).filter((d) => d !== key) });

  const DayList = ({ list, days, emptyText, color, icon }) => (
    <Stack spacing={1} sx={{ mt: 1 }}>
      {days.length === 0 && (
        <Typography variant="body2" color="text.secondary">{emptyText}</Typography>
      )}
      {days.map((key) => (
        <Stack key={key} direction="row" spacing={1} alignItems="center">
          <Chip size="small" color={color} icon={icon} label={prettyDate(key)} variant="outlined" />
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: "capitalize" }}>
            {weekdayOf(key)}
          </Typography>
          {list === "closedDays" && holidaySet.has(key) && (
            <Typography variant="caption" color="text.secondary">
              · ya es festivo de ley, no hace falta
            </Typography>
          )}
          <Box sx={{ flex: 1 }} />
          <Tooltip title="Quitar">
            <IconButton size="small" onClick={() => remove(list, key)}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ))}
    </Stack>
  );

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>Calendario de la institución</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Los términos legales de la aplicación se cuentan en <strong>días hábiles</strong>: se
          excluyen sábados, domingos y los 18 festivos de ley (que el sistema ya calcula, incluyendo
          los que la Ley Emiliani corre al lunes siguiente). Aquí se agregan los días en que{" "}
          <strong>esta institución</strong> no trabaja, y los días en que sí trabaja a pesar del
          calendario.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={kind}
            onChange={(_, v) => v && setKind(v)}
          >
            <ToggleButton value="closedDays">No se trabaja</ToggleButton>
            <ToggleButton value="openDays">Sí se trabaja</ToggleButton>
          </ToggleButtonGroup>
          <TextField
            size="small"
            type="date"
            label="Fecha"
            InputLabelProps={{ shrink: true }}
            value={newDay}
            onChange={(e) => setNewDay(e.target.value)}
          />
          <Button startIcon={<AddIcon />} onClick={add} variant="outlined">Agregar</Button>
        </Stack>

        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2">Días en que la institución no trabaja</Typography>
          <Typography variant="caption" color="text.secondary">
            Se descuentan de todos los plazos, igual que un festivo.
          </Typography>
          <DayList
            list="closedDays"
            days={closedDays}
            color="warning"
            icon={<EventBusyIcon />}
            emptyText="Sin días adicionales. Solo aplican los festivos de ley."
          />
        </Box>

        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2">Días hábiles excepcionales</Typography>
          <Typography variant="caption" color="text.secondary">
            Se cuentan como hábiles aunque caigan en festivo o fin de semana. Tienen prioridad sobre
            todo lo demás.
          </Typography>
          <DayList
            list="openDays"
            days={openDays}
            color="success"
            icon={<EventAvailableIcon />}
            emptyText="Ninguno."
          />
        </Box>

        <Divider sx={{ my: 3 }} />

        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap">
          <Typography variant="subtitle2">Festivos de ley que el sistema ya aplica</Typography>
          <TextField
            size="small"
            type="number"
            label="Año"
            sx={{ width: 110 }}
            inputProps={{ min: 2000, max: 2100 }}
            value={year}
            onChange={(e) => setYear(Number(e.target.value) || YEAR_NOW)}
          />
        </Stack>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" },
            gap: 1,
          }}
        >
          {holidays.map((h) => (
            <Stack key={h.date} direction="row" spacing={1} alignItems="baseline">
              <Typography variant="body2" sx={{ fontFamily: "monospace" }}>{h.date}</Typography>
              <Typography variant="body2" color="text.secondary">{h.name}</Typography>
              {h.moved && (
                <Tooltip title="La Ley Emiliani lo traslada al lunes siguiente">
                  <Typography variant="caption" color="text.secondary">(lunes)</Typography>
                </Tooltip>
              )}
            </Stack>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
}
